import { CanvasTexture, RepeatWrapping, SRGBColorSpace } from 'three';
import { createRandom } from './random.js';

/**
 * A cheap procedurally generated speckle texture. Useful for prototyping
 * surfaces without shipping texture files.
 */
export function createNoiseTexture({
  size = 256,
  base = '#c8ab7c',
  speckle = '#b09465',
  count = 3600,
  repeat = 24,
  seed = 7,
  anisotropy = 4,
} = {}) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d');
  const random = createRandom(seed);

  ctx.fillStyle = base;
  ctx.fillRect(0, 0, size, size);

  for (let i = 0; i < count; i += 1) {
    const x = random() * size;
    const y = random() * size;
    const radius = random.range(0.4, 1.5);
    ctx.globalAlpha = random.range(0.08, 0.3);
    ctx.fillStyle = speckle;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  // A few larger pebbles break up the uniformity.
  ctx.globalAlpha = 0.22;
  for (let i = 0; i < 40; i += 1) {
    const x = random() * size;
    const y = random() * size;
    ctx.beginPath();
    ctx.ellipse(x, y, random.range(1, 2.4), random.range(0.8, 1.8), random() * Math.PI, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.globalAlpha = 1;

  const texture = new CanvasTexture(canvas);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.repeat.set(repeat, repeat);
  texture.colorSpace = SRGBColorSpace;
  texture.anisotropy = anisotropy;
  texture.needsUpdate = true;

  return texture;
}

let smokeTexture = null;

/**
 * Soft radial puff used by smoke sprites (vehicle hood, explosions). Cached so
 * every effect shares one canvas texture.
 */
export function getSmokeTexture() {
  if (smokeTexture) return smokeTexture;

  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  const gradient = context.createRadialGradient(
    size * 0.5,
    size * 0.5,
    0,
    size * 0.5,
    size * 0.5,
    size * 0.5,
  );
  gradient.addColorStop(0, 'rgba(255, 255, 255, 0.95)');
  gradient.addColorStop(0.45, 'rgba(255, 255, 255, 0.55)');
  gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
  context.fillStyle = gradient;
  context.fillRect(0, 0, size, size);

  smokeTexture = new CanvasTexture(canvas);
  smokeTexture.colorSpace = SRGBColorSpace;
  return smokeTexture;
}
